import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface ShopifyLineItem {
  id: string;
  title: string;
  variantTitle: string | null;
  quantity: number;
  price: string;
  sku: string | null;
  vendor: string | null;
  productId: string | null;
  variantId: string | null;
}

interface ShopifyOrder {
  id: string;
  name: string;
  email: string | null;
  createdAt: string;
  updatedAt: string;
  financialStatus: string;
  fulfillmentStatus: string | null;
  totalPrice: string;
  subtotalPrice: string;
  currency: string;
  lineItems: ShopifyLineItem[];
  customer: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface ActionTestResponse {
  data?: {
    result?: {
      success: boolean;
      data?: {
        orders?: ShopifyOrder[];
      };
      error?: {
        code: string;
        message: string;
      };
    };
  };
  error?: {
    message: string;
  };
}

/**
 * Fetch recent orders from the Shopify store via shopify-list-orders action.
 * Used as fallback for shopify-order-created and shopify-order-paid triggers.
 *
 * Fetches real orders from the store so users can test with actual order data.
 *
 * Requires: Shopify connector with read_orders scope.
 */
export const shopifyOrderFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { connectorId, triggerPayload } = ctx;

  if (!connectorId) throw new Error("No Shopify store connected. Add a Shopify connector to test this trigger.");

  const shopDomain = (triggerPayload.shopDomain as string) ?? "";

  // For order-paid triggers, only fetch paid orders; otherwise fetch all recent orders
  const inputs: Record<string, unknown> = { limit: 5 };
  if (ctx.triggerType === "shopify-order-paid") {
    inputs.financialStatus = "PAID";
  }

  const response = await authenticatedFetch<ActionTestResponse>(`${V2_ENDPOINT}/v1/actions/shopify-list-orders/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs,
      connector: { id: connectorId, type: "shopify" },
    }),
    signal: AbortSignal.timeout(30_000),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch orders from Shopify (HTTP ${response.status}).`);
  }

  const json = await response.json();

  // Surface action-level errors (missing scope, action not found, etc.)
  const result = json.data?.result;
  if (result && !result.success) {
    throw new Error(result.error?.message ?? "Shopify action failed. Check connector scopes include read_orders.");
  }

  // Surface top-level API errors
  if (json.error?.message) {
    throw new Error(json.error.message);
  }

  const orders: ShopifyOrder[] = result?.data?.orders ?? [];
  if (orders.length === 0) return [];

  return orders.map(order => {
    const firstLineItem = order.lineItems?.[0];

    return {
      triggeredAt: order.createdAt,
      topic: ctx.triggerType === "shopify-order-paid" ? "orders/paid" : "orders/create",
      shopDomain,
      order: {
        id: order.id,
        name: order.name,
        email: order.email ?? order.customer?.email ?? null,
        created_at: order.createdAt,
        updated_at: order.updatedAt,
        total_price: order.totalPrice,
        subtotal_price: order.subtotalPrice,
        currency: order.currency,
        financial_status: order.financialStatus.toLowerCase(),
        fulfillment_status: order.fulfillmentStatus?.toLowerCase() ?? null,
        line_items: order.lineItems.map(li => ({
          id: li.id,
          title: li.title,
          variant_title: li.variantTitle ?? "Default",
          quantity: li.quantity,
          price: li.price,
          sku: li.sku ?? null,
          vendor: li.vendor ?? null,
          product_id: li.productId ?? null,
          variant_id: li.variantId ?? null,
        })),
        customer: order.customer
          ? {
              id: order.customer.id,
              email: order.customer.email ?? null,
              first_name: order.customer.firstName ?? null,
              last_name: order.customer.lastName ?? null,
            }
          : null,
      },
      _orderName: order.name,
      _totalPrice: `$${order.totalPrice}`,
      _productTitle: firstLineItem?.title ?? "No items",
      _status: order.financialStatus.toLowerCase(),
    };
  });
};
